import { useEffect, useRef } from "react";
import { trackBackendEvent } from "../lib/api";
import { SubscriptionStatus } from "../types";

/**
 * `console_opened` properties at schema_version 1 — mirrors `ConsoleOpenedV1` in
 * packages/events-schema/events/extension/console-opened-v1.avsc. Closed sets only: no page, no URL,
 * no organization name or id (the backend attributes the row to the signed-in email).
 */
export interface ConsoleOpenedProperties {
  client: "web";
  tenant_type: string | null;
  role: string | null;
}

const TENANT_TYPES: ReadonlySet<string> = new Set(["personal", "family", "business", "premium"]);
const ROLES: ReadonlySet<string> = new Set(["owner", "admin", "member"]);
/**
 * ENG-3869 (Colin, 2026-10-16): the event is sent for these roles only. The Owner-return metric needs
 * owners and admins, and each row carries the signed-in email, so a member's open would be a
 * per-person signal nothing reads. The backend drops the rest too, for console builds from before this.
 */
const SENT_FOR_ROLES: ReadonlySet<string> = new Set(["owner", "admin"]);

/** Anything outside the closed set is sent as null rather than as whatever the server said. */
export function consoleOpenedProperties(status: SubscriptionStatus | null): ConsoleOpenedProperties {
  const tenantType = status?.tenantType;
  const role = status?.role;
  return {
    client: "web",
    tenant_type: tenantType && TENANT_TYPES.has(tenantType) ? tenantType : null,
    role: role && ROLES.has(role) ? role : null,
  };
}

/**
 * ENG-3255: one `console_opened` per page load per signed-in account, once /subscription-status has
 * answered, so the Haven Family D7/D30 Owner-return metric can see an Owner come back. Nothing the
 * console emitted before this said that someone had opened it at all — `protected_sites_viewed` fires
 * on one page only.
 *
 * Keyed on the account, not on the mount: a sign-out and a different sign-in in the same tab is a
 * second open. A status refresh, a page change or a tenant switch inside the same load is not. Skipped
 * while the page is serving the extension's sign-in flow ([skip]), which is a login, not a visit.
 *
 * Sent only when the viewer is an owner or admin of the organization the console first shows
 * (ENG-3869). That is decided once per account per load, like the event itself: an account the console
 * first shows as a member, or with no organization, sends nothing for the load, even after a switch.
 */
export function useConsoleOpenedEvent(args: {
  uid: string | null;
  subscriptionLoaded: boolean;
  subscriptionStatus: SubscriptionStatus | null;
  skip: boolean;
}): void {
  const { uid, subscriptionLoaded, subscriptionStatus, skip } = args;
  const sentFor = useRef<string | null>(null);
  // Read at fire time rather than named as a dependency: a later refresh of the status must not
  // re-fire the event (the same pattern as useExtension's `latest`).
  const latestStatus = useRef(subscriptionStatus);
  latestStatus.current = subscriptionStatus;
  useEffect(() => {
    if (skip || !uid || !subscriptionLoaded || sentFor.current === uid) return;
    sentFor.current = uid;
    const properties = consoleOpenedProperties(latestStatus.current);
    if (properties.role === null || !SENT_FOR_ROLES.has(properties.role)) return;
    void trackBackendEvent("console_opened", { ...properties });
  }, [uid, subscriptionLoaded, skip]);
}
